import { Head, router } from '@inertiajs/react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Typography,
} from '@mui/material'
import AppsIcon from '@mui/icons-material/Apps'
import SettingsIcon from '@mui/icons-material/Settings'
import React, { useState } from 'react'
import AppLayout from '../../components/layouts/AppLayout'
import type { DashboardEntry } from '../../types'

interface Props {
  entries: DashboardEntry[]
}

export default function Index({ entries }: Props) {
  const [unfollowingIds, setUnfollowingIds] = useState<Set<number>>(new Set())

  const handleUnfollow = async (entryId: number) => {
    setUnfollowingIds(prev => new Set(prev).add(entryId))
    try {
      await fetch(`/app_entries/${entryId}/follow`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
          'Accept': 'application/json',
        },
      })
      router.reload()
    } finally {
      setUnfollowingIds(prev => {
        const next = new Set(prev)
        next.delete(entryId)
        return next
      })
    }
  }

  return (
    <AppLayout title="Dashboard">
      <Head title="Dashboard" />
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Followed Apps</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => router.visit('/dashboard/notification_preferences')}
          >
            Notification Preferences
          </Button>
        </Box>

        {entries.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <AppsIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No followed apps yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Browse the app store and follow apps to see them here.
              </Typography>
              <Button variant="contained" onClick={() => router.visit('/apps')}>
                Browse Apps
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {entries.map((entry) => (
              <Grid key={entry.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      {entry.logoUrl ? (
                        <Box
                          component="img"
                          src={entry.logoUrl}
                          alt={entry.name}
                          sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover' }}
                        />
                      ) : (
                        <Box sx={{
                          width: 48, height: 48, borderRadius: 1,
                          bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <AppsIcon sx={{ color: 'grey.500' }} />
                        </Box>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                          {entry.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          by {entry.vendorName}
                        </Typography>
                      </Box>
                    </Box>
                    {entry.shortDescription && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {entry.shortDescription}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {entry.latestVersion && (
                        <Chip label={`v${entry.latestVersion}`} size="small" color="primary" />
                      )}
                    </Box>
                  </CardContent>
                  <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => router.visit(`/apps/${entry.vendorSlug}/${entry.slug}`)}
                      sx={{ flex: 1 }}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleUnfollow(entry.id)}
                      disabled={unfollowingIds.has(entry.id)}
                    >
                      Unfollow
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </AppLayout>
  )
}
